import React, { useState, useEffect } from 'react';
import { UserProfile } from '../types';
import {
  X,
  MessageSquare,
  Phone,
  Video,
  Shield,
  Calendar,
  MapPin,
  Sparkles,
  UserPlus,
  Ban,
  Flag,
  Check,
  Crown,
  Trash2,
  Eraser,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';
import { ConfirmationModal, ConfirmationType } from './ConfirmationModal';

interface UserProfileModalProps {
  username: string;
  currentUser: UserProfile;
  conversationId?: string | null;
  onClose: () => void;
  onStartChat: (username: string) => void;
  onStartCall: (targetUser: { username: string; displayName: string; avatar: string }, type: 'voice' | 'video') => void;
  onDeleteConversation?: (convId: string) => void;
  onClearConversation?: (convId: string) => void;
  onBlockUser?: (targetUsername: string) => void;
  onUnblockUser?: (targetUsername: string) => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  username,
  currentUser,
  conversationId,
  onClose,
  onStartChat,
  onStartCall,
  onDeleteConversation,
  onClearConversation,
  onBlockUser,
  onUnblockUser,
}) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [inCircle, setInCircle] = useState(false);
  const [isReported, setIsReported] = useState(false);

  // Active confirmation dialog state
  const [activeConfirmation, setActiveConfirmation] = useState<{
    isOpen: boolean;
    type: ConfirmationType;
    title: string;
    description: string;
    confirmText?: string;
    action: () => void;
  } | null>(null);

  const isBlocked = Boolean(currentUser.blockedUsers?.includes(username));

  useEffect(() => {
    fetch(`/api/users/${username}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.user) setProfile(data.user);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [username]);

  if (loading || !profile) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md">
        <span className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const handlePromptBlock = () => {
    soundEffects.playTap();
    if (isBlocked) {
      setActiveConfirmation({
        isOpen: true,
        type: 'unblock_user',
        title: 'إلغاء حظر المستخدم',
        description: `هل ترغب في إلغاء حظر @${profile.username}؟ سيتمكن من التواصل معك مجدداً على وتين.`,
        confirmText: 'نعم، فك الحظر',
        action: () => {
          onUnblockUser?.(profile.username);
          setActiveConfirmation(null);
        },
      });
    } else {
      setActiveConfirmation({
        isOpen: true,
        type: 'block_user',
        title: 'حظر المستخدم',
        description: `هل أنت متأكد من رغبتك في حظر @${profile.username}؟ لن يتمكن من إرسال رسائل إليك أو الاتصال بك، ولن تظهر حالتك لديه.`,
        confirmText: 'نعم، احظر الشخص',
        action: () => {
          onBlockUser?.(profile.username);
          setActiveConfirmation(null);
        },
      });
    }
  };

  const handlePromptClearChat = () => {
    soundEffects.playTap();
    setActiveConfirmation({
      isOpen: true,
      type: 'clear_chat',
      title: 'مسح سجل الدردشة',
      description: `هل أنت متأكد من مسح جميع الرسائل والوسائط في الدردشة مع @${profile.username}؟ هذا الإجراء لا يمكن التراجع عنه.`,
      confirmText: 'نعم، امسح الدردشة',
      action: () => {
        if (conversationId && onClearConversation) {
          onClearConversation(conversationId);
        }
        setActiveConfirmation(null);
      },
    });
  };

  const handlePromptDeleteChat = () => {
    soundEffects.playTap();
    setActiveConfirmation({
      isOpen: true,
      type: 'delete_conversation',
      title: 'حذف المحادثة نهائياً',
      description: `هل أنت متأكد من رغبتك في حذف محادثتك مع @${profile.username} بالكامل وإزالتها من قائمة الدردشات؟`,
      confirmText: 'نعم، احذف المحادثة',
      action: () => {
        if (conversationId && onDeleteConversation) {
          onDeleteConversation(conversationId);
          onClose();
        }
        setActiveConfirmation(null);
      },
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl font-['Cairo'] select-none">
        <div className="relative w-full max-w-md bg-[#0e1422] border border-slate-800 rounded-3xl overflow-hidden shadow-2xl">
          {/* Cover Banner */}
          <div className="h-28 bg-gradient-to-r from-emerald-600/40 via-teal-600/30 to-indigo-600/40 relative">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition"
              title="إغلاق"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Profile Card Header */}
          <div className="px-6 pb-6 relative -mt-12 text-right">
            <div className="flex items-end justify-between mb-3">
              <div className="relative">
                <img
                  src={profile.avatar}
                  alt={profile.displayName}
                  className={`w-20 h-20 rounded-3xl object-cover ring-4 shadow-xl ${
                    profile.isPremium ? 'ring-amber-400' : 'ring-[#0e1422]'
                  }`}
                />
                {profile.isPremium && (
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black shadow-lg">
                    👑
                  </span>
                )}
                <span
                  className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-[#0e1422] ${
                    isBlocked ? 'bg-rose-500' : 'bg-emerald-500'
                  }`}
                />
              </div>

              {/* Quick Action Buttons */}
              {profile.username !== currentUser.username && (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => {
                      onStartChat(profile.username);
                      onClose();
                    }}
                    className="p-2.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow transition"
                    title="بدء محادثة"
                  >
                    <MessageSquare className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      onStartCall(profile, 'voice');
                      onClose();
                    }}
                    disabled={isBlocked}
                    className={`p-2.5 rounded-2xl border text-white transition ${
                      isBlocked
                        ? 'bg-slate-800/40 border-slate-800 text-slate-500 cursor-not-allowed'
                        : 'bg-[#141b27] hover:bg-[#1c2637] border-slate-800'
                    }`}
                    title={isBlocked ? 'المستخدم محظور' : 'اتصال صوتي'}
                  >
                    <Phone className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      onStartCall(profile, 'video');
                      onClose();
                    }}
                    disabled={isBlocked}
                    className={`p-2.5 rounded-2xl border text-white transition ${
                      isBlocked
                        ? 'bg-slate-800/40 border-slate-800 text-slate-500 cursor-not-allowed'
                        : 'bg-[#141b27] hover:bg-[#1c2637] border-slate-800'
                    }`}
                    title={isBlocked ? 'المستخدم محظور' : 'اتصال فيديو'}
                  >
                    <Video className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-black text-white">{profile.displayName}</h2>
              {profile.isPremium && (
                <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-black border border-amber-500/40 flex items-center gap-1">
                  <Crown className="w-3 h-3 text-amber-400 fill-amber-400" />
                  <span>وتين Premium 👑</span>
                </span>
              )}
              {profile.isVerified && (
                <span className="px-2 py-0.2 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold border border-emerald-500/30">
                  موثّق ✓
                </span>
              )}
              {isBlocked && (
                <span className="px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 text-[10px] font-black border border-rose-500/40 flex items-center gap-1">
                  <Ban className="w-3 h-3 text-rose-400" />
                  <span>محظور</span>
                </span>
              )}
            </div>
            <span className="text-xs text-emerald-400 font-mono font-semibold block mt-0.5">
              @{profile.username}
            </span>

            {/* Pulse Status */}
            {profile.pulseStatus && (
              <div className="mt-3 p-2.5 rounded-2xl bg-[#141c2c] border border-slate-800/80 flex items-center gap-2 text-xs text-slate-200">
                <span className="text-base">{profile.pulseStatus.emoji || '✨'}</span>
                <span className="font-semibold">{profile.pulseStatus.text || 'متاح على وتين'}</span>
              </div>
            )}

            {/* Bio */}
            <p className="text-xs text-slate-300 mt-3 leading-relaxed whitespace-pre-wrap">
              {profile.bio || 'مستخدم وتين مميز 🌿'}
            </p>

            {/* Info row */}
            <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs text-slate-400">
              <div className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                <span>{profile.country || 'المملكة العربية السعودية'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-emerald-400" />
                <span>انضم {new Date(profile.joinedDate).toLocaleDateString('ar-SA')}</span>
              </div>
            </div>

            {/* Moderation & Chat Controls (حظر، مسح الدردشة، حذف المحادثة) */}
            {profile.username !== currentUser.username && (
              <div className="mt-4 pt-3 border-t border-slate-800/80 space-y-2">
                <div className="text-[11px] font-bold text-slate-400 mb-2">إدارة الخصوصية والمحادثة:</div>
                <div className="grid grid-cols-3 gap-2">
                  {/* Block / Unblock Button */}
                  <button
                    type="button"
                    onClick={handlePromptBlock}
                    className={`p-2 rounded-2xl border flex flex-col items-center justify-center gap-1 transition ${
                      isBlocked
                        ? 'bg-teal-500/15 border-teal-500/40 text-teal-300 hover:bg-teal-500/25'
                        : 'bg-rose-950/20 border-rose-500/30 text-rose-300 hover:bg-rose-900/30'
                    }`}
                  >
                    {isBlocked ? (
                      <ShieldCheck className="w-4 h-4 text-teal-400" />
                    ) : (
                      <Ban className="w-4 h-4 text-rose-400" />
                    )}
                    <span className="text-[10px] font-bold">
                      {isBlocked ? 'فك الحظر' : 'حظر الشخص'}
                    </span>
                  </button>

                  {/* Clear Chat Button */}
                  <button
                    type="button"
                    onClick={handlePromptClearChat}
                    className="p-2 rounded-2xl border bg-amber-950/20 border-amber-500/30 text-amber-300 hover:bg-amber-900/30 flex flex-col items-center justify-center gap-1 transition"
                  >
                    <Eraser className="w-4 h-4 text-amber-400" />
                    <span className="text-[10px] font-bold">مسح الدردشة</span>
                  </button>

                  {/* Delete Conversation Button */}
                  <button
                    type="button"
                    onClick={handlePromptDeleteChat}
                    className="p-2 rounded-2xl border bg-red-950/20 border-red-500/30 text-red-300 hover:bg-red-900/30 flex flex-col items-center justify-center gap-1 transition"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                    <span className="text-[10px] font-bold">حذف المحادثة</span>
                  </button>
                </div>
              </div>
            )}

            {/* Circle & Report Action */}
            {profile.username !== currentUser.username && (
              <div className="mt-3 pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
                <button
                  onClick={() => {
                    setInCircle(!inCircle);
                    soundEffects.playTap();
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border transition ${
                    inCircle
                      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300 font-bold'
                      : 'bg-[#141b27] border-slate-800 text-slate-300 hover:text-white'
                  }`}
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  <span>{inCircle ? 'في دائرة المقربين ✓' : 'إضافة لدائرة وتين'}</span>
                </button>

                <button
                  onClick={() => setIsReported(true)}
                  className="text-slate-500 hover:text-rose-400 flex items-center gap-1 text-[11px]"
                >
                  <Flag className="w-3.5 h-3.5" />
                  <span>{isReported ? 'تم إرسال البلاغ' : 'إبلاغ'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {activeConfirmation && (
        <ConfirmationModal
          isOpen={activeConfirmation.isOpen}
          type={activeConfirmation.type}
          title={activeConfirmation.title}
          description={activeConfirmation.description}
          confirmText={activeConfirmation.confirmText}
          isDestructive={activeConfirmation.type !== 'unblock_user'}
          onConfirm={activeConfirmation.action}
          onCancel={() => setActiveConfirmation(null)}
        />
      )}
    </>
  );
};
